import { Link, useParams } from "react-router-dom";
import { facts, getArticle, categoryName, verdictMeta } from "@/mocks/articles";
import { VerdictBadge, CardMeta } from "@/components/base/ArticleCard";
import Breadcrumb, { Footer } from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import JsonLd from "@/components/base/JsonLd";
import useSeo from "@/hooks/useSeo";

export default function ArticleDetail() {
  const { slug = "" } = useParams();
  const article = getArticle(slug);

  useSeo(
    article
      ? `${article.title} · 爆料真实性核查 · 辨真社`
      : "没有找到该核验 · 辨真社",
    article ? `${article.summary} 辨真社通过证据链与多方对撞给出${verdictMeta[article.verdict].label}结论。` : undefined
  );

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="mx-auto flex max-w-3xl flex-col items-center px-4 py-28 text-center">
          <i className="ri-question-mark text-6xl text-foreground-300" />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这条核验</h1>
          <Link to="/topics" className="mt-6 inline-flex h-11 items-center justify-center rounded-md bg-primary-600 px-6 text-sm font-semibold text-background-50">
            返回事件解析
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const related = facts.filter((a) => a.slug !== article.slug).slice(0, 4);

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.summary,
          inLanguage: "zh-CN",
          datePublished: `${article.date.replace(/\./g, "-")}`,
          image: article.cover,
          mainEntityOfPage: `${window.location.origin}/article/${article.slug}`,
          author: { "@type": "Organization", name: "辨真社" },
          publisher: { "@type": "Organization", name: "辨真社" },
        }}
      />
      <Navbar />
      <main>
        <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "事件解析", to: "/topics" },
              { label: categoryName(article.category), to: `/topics?c=${article.category}` },
              { label: "详情" },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-3">
            {/* 正文区 */}
            <article className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-2.5">
                <VerdictBadge verdict={article.verdict} />
                <span className="inline-flex items-center rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                  {categoryName(article.category)}
                </span>
              </div>
              <h1 className="mt-5 font-heading text-3xl font-black leading-snug text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <div className="mt-5 border-y border-background-200 py-3">
                <CardMeta date={article.date} minutes={article.readMinutes} heat={article.heat} />
              </div>

              {/* 传闻 */}
              <div className="mt-8 rounded-lg border border-secondary-200 bg-secondary-100/60 p-5 md:p-6">
                <div className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-200 text-secondary-900"><i className="ri-volume-up-line" /></span>
                  网络传闻
                </div>
                <p className="mt-3 text-base leading-relaxed text-foreground-800">{article.claim}</p>
              </div>

              {/* 引言 */}
              <p className="mt-8 text-lg leading-loose text-foreground-800">{article.opening}</p>

              {/* 证据链 */}
              <section className="mt-10">
                <h2 className="flex items-center gap-2.5 font-heading text-2xl font-black text-foreground-950">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700"><i className="ri-focus-3-line" /></span>
                  证据链梳理
                </h2>
                <div className="mt-6 space-y-4">
                  {article.checks.map((c, i) => (
                    <div key={i} className="relative flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground-950 font-heading text-sm font-black text-background-50">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <div className="font-heading text-base font-bold text-primary-800">线索 · {c.kind}</div>
                        <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{c.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* 多角度解析 */}
              <section className="mt-10">
                <h2 className="flex items-center gap-2.5 font-heading text-2xl font-black text-foreground-950">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-700"><i className="ri-search-eye-line" /></span>
                  深度解析
                </h2>
                <div className="mt-6 space-y-6">
                  {article.angles.map((p, i) => (
                    <div key={i}>
                      <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                        <span className="text-primary-600">0{i + 1}</span>
                      </h3>
                      <p className="mt-2 text-base leading-loose text-foreground-800">{p}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* 结论 */}
              <section className="mt-10">
                <div className={`rounded-lg border p-6 ${verdictMeta[article.verdict].tone} bg-opacity-10`}>
                  <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                    <i className={article.verdict === "verified" ? "ri-check-double-line" : article.verdict === "debunked" ? "ri-error-warning-line" : "ri-time-line"} />
                    核验结论
                  </h2>
                  <p className="mt-3 text-base leading-loose text-foreground-800">{article.conclusion}</p>
                </div>
                <p className="mt-4 text-xs leading-relaxed text-foreground-500">
                  提示：本站结论基于当前可获得的公开信息，仅供参考；相关争议请以官方机构与权威渠道发布为准。
                </p>
              </section>
            </article>

            {/* 侧栏 */}
            <aside className="space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 lg:sticky lg:top-24">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-database-2-line text-primary-600" /> 档案速览
                </h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between border-b border-background-100 pb-2.5">
                    <dt className="text-foreground-600">事件分类</dt>
                    <dd className="font-medium text-foreground-900">{categoryName(article.category)}</dd>
                  </div>
                  <div className="flex items-center justify-between border-b border-background-100 pb-2.5">
                    <dt className="text-foreground-600">核验结论</dt>
                    <dd className="font-medium text-primary-700">{verdictMeta[article.verdict].label}</dd>
                  </div>
                  <div className="flex items-center justify-between border-b border-background-100 pb-2.5">
                    <dt className="text-foreground-600">证据线索</dt>
                    <dd className="font-medium text-foreground-900">{article.checks.length} 条</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-600">更新日期</dt>
                    <dd className="font-medium text-foreground-900">{article.date}</dd>
                  </div>
                </dl>
                <div className="mt-5 flex flex-col gap-2.5">
                  <Link to="/guide" className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md bg-foreground-950 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-foreground-800">
                    学习辨别方法
                  </Link>
                  <Link to="/topics" className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-background-200 px-4 text-sm font-medium text-foreground-800 transition-colors hover:border-secondary-300">
                    浏览更多事件
                  </Link>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">你可能想查</h2>
                <div className="mt-4 space-y-3">
                  {related.map((a) => (
                    <Link key={a.slug} to={`/article/${a.slug}`} className="group flex gap-3">
                      <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-100">
                        <img src={a.cover} alt={a.title} loading="lazy" className="h-full w-full object-cover object-top" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <VerdictBadge verdict={a.verdict} size="sm" />
                        </div>
                        <p className="mt-1.5 line-clamp-2 text-sm leading-snug text-foreground-800 group-hover:text-primary-700">
                          {a.title}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}